import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useUserContext } from '../../context/user';
import { useUsersQuery } from '../../hooks/useUsersQuery'
import useExportData from '../../hooks/useExportData';
import { useSorting } from '../../hooks/useSorting';
import useDebounce from '../../hooks/useDebounce';
import { usePaginationTextReplace } from '../../hooks/usePaginationTextReplace';
import useCleanPaginationURL from '../../hooks/useCleanPaginationURL';
import { blockUsers, deleteUsers, exportUsers, unblockUsers } from '../../services/users';
import { isAllowAction } from '../../services/permissions/utils';
import { User, UsersFilter, UserOrderingField } from '../../types/api/users';
import { formatDate } from '../../helpers/dates';
import Segment from '../navigation/Segment/Segment';
import { usersTabsData } from '../navigation/Segment/Segment+Data';
import { Placeholder, createErrorPlaceholder } from '../basic';
import RegularPageLayout from '../../layouts/RegularPageLayout';
import { useAlert } from '../../context/alert';
import { pageSize } from '../../services/settings';
import { TextInput, Stack, Checkbox, Label, IconButton, Button, ActionMenu, ActionList, Text, Pagination } from '@primer/react'
import { Table, DataTable } from '@primer/react/experimental'
import { SearchIcon, FilterIcon, DownloadIcon, XCircleIcon, XCircleFillIcon, XIcon, BlockedIcon, TrashIcon, CodeOfConductIcon } from '@primer/octicons-react'
import TableActionsBanner from '../common/TableActionsBanner';
import { usersTableConfig } from './UsersList+Table';
import { locale } from '../../helpers/strings';
import { filterItems, emptyListDescription } from './UsersList+Data';
import { CheckUserStatus } from './UsersList+CheckUserStatus';

const EMPTY_NUMBERS_ARRAY: number[] = [];
const EMPTY_STRINGS_ARRAY: string[] = [];
const EMPTY_SET = new Set<string>();

export default function UsersList() {
    type UserAction = 'block' | 'unblock' | 'delete';

    const { profile } = useUserContext();
    const navigate = useNavigate();

    const [pageIndex, setPageIndex] = React.useState(1)

    const [searchQuery, setSearchQuery] = useState<string>('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500)

    const [selectedRows, setSelectedRows] = useState<Set<string>>(EMPTY_SET);
    const [actionsAlertId, setActionsAlertId] = useState<string | undefined>(undefined);

    const [selectedFilter, setSelectedFilter] = useState<UsersFilter>('none');
    const selectedIndex = filterItems.findIndex(item => item.filter === selectedFilter);

    const queryClient = useQueryClient();

    const { isLoading: isLoadingExport, exportAction } = useExportData();
    const { setSuccess, setInfo, setError, addCustomAlert, updateAlert, removeAlert } = useAlert();

    const {
        orderingParam,
        handleSort,
        getSortIcon,
        getSortText
    } = useSorting<UserOrderingField>('username');
    const orderingParams: UserOrderingField = orderingParam as UserOrderingField;

    useEffect(() => {
        setPageIndex(1);
        setSelectedRows(EMPTY_SET);
    }, [debouncedSearchQuery]);

    const filterQuery = useMemo(() => {
        switch (selectedFilter) {
            case 'active':
                return false;
            case 'blocked':
                return true;
            case 'none':
                return undefined;
        }
    }, [selectedFilter]);
    const {
        isLoading,
        data,
        refetch,
        isFetched,
        isError
    } = useUsersQuery(
        debouncedSearchQuery,
        pageIndex,
        pageSize,
        filterQuery,
        orderingParams
    );

    useCleanPaginationURL();
    usePaginationTextReplace()

    useEffect(() => {
        setSelectedRows(EMPTY_SET);
        setPageIndex(1);
        refetch()
    }, [selectedFilter]);

    useEffect(() => {
        setSelectedRows(EMPTY_SET);
        refetch()
    }, [pageIndex]);

    // USERS ACTIONS
    const useUserAction = (action: UserAction, actionsAlertId?: string) => {
        return useCallback(() => {
            let selectedItems = selectedRowsRef.current;

            if (selectedItems && selectedItems.size > 0) {
                const ids = Array.from(selectedItems);

                switch (action) {
                    case 'block':
                        blockUsersByIds({ ids });
                        break;
                    case 'unblock':
                        unblockUsersByIds({ ids });
                        break;
                    case 'delete':
                        deleteUsersByIds({ ids });
                        break;
                    default:
                        console.error('Unknown action:', action);
                }
            }
        }, [action]);
    };

    const blockSelectedUsers = useUserAction('block', actionsAlertId);
    const unblockSelectedUsers = useUserAction('unblock', actionsAlertId);
    const deleteSelectedUsers = useUserAction('delete', actionsAlertId);

    const handleUsersActionResult = (
        count: number,
        singularMessage: string,
        pluralMessage: string
    ) => {
        // Update UI
        dismissTableActionAlert()
        setSelectedRows(EMPTY_SET);
        refetch();
        // Alert
        if (count === 1) {
            setInfo(singularMessage);
        } else {
            setInfo(pluralMessage);
        }
    };

    // BLOCK USERS
    const { mutate: blockUsersByIds } = useMutation({
        mutationFn: ({ ids }: {
            ids: string[],
        }) => blockUsers({ userIds: ids }),
        onSuccess: (data) => {
            handleUsersActionResult(data.count, 'Пользователь заблокирован', data.count + ' пользователя заблокированы')
        }
    });

    function createConfirmBlockAlertProps(count: number) {
        return {
            count: count,
            title: 'Точно хотите заблокировать?',
            buttons: [
                <Button key="clear-button" variant="default" onClick={showTableActionsAlert}>
                    Нет
                </Button>,
                <Button key="clear-button" variant="default" onClick={blockSelectedUsers}>
                    Да
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
                setSelectedRows(EMPTY_SET);
            }
        }
    }

    const showConfirmBlockAlert = useCallback(() => {
        let selectedItemsCount = selectedRowsRef.current.size
        const props = createConfirmBlockAlertProps(selectedItemsCount)
        showUpdatedAlert(props)
    }, [actionsAlertId]);

    // UNBLOCK USERS
    const { mutate: unblockUsersByIds } = useMutation({
        mutationFn: ({ ids }: {
            ids: string[],
        }) => unblockUsers({ userIds: ids }),
        onSuccess: (data) => {
            handleUsersActionResult(data.count, 'Пользователь разблокирован', data.count + ' пользователя разблокированы')
        }
    });

    function createConfirmUnblockAlertProps(count: number) {
        return {
            count: count,
            title: 'Точно хотите разблокировать?',
            buttons: [
                <Button key="clear-button" variant="default" onClick={showTableActionsAlert}>
                    Нет
                </Button>,
                <Button key="clear-button" variant="default" onClick={unblockSelectedUsers}>
                    Да
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
                // Uncheck table
                setSelectedRows(EMPTY_SET);
            }
        }
    }

    const showConfirmUnblockAlert = useCallback(() => {
        let selectedItemsCount = selectedRowsRef.current.size
        const props = createConfirmUnblockAlertProps(selectedItemsCount)
        showUpdatedAlert(props)
    }, [actionsAlertId]);

    // DELETE USERS
    const { mutate: deleteUsersByIds } = useMutation({
        mutationFn: ({ ids }: {
            ids: string[],
        }) => deleteUsers({ userIds: ids }),
        onSuccess: (data) => {
            handleUsersActionResult(data.count, 'Пользователь удалён', data.count + ' пользователя удалены')
        }
    });

    function createConfirmDeleteAlertProps(count: number) {
        return {
            count: count,
            title: 'Точно хотите удалить?',
            buttons: [
                <Button key="clear-button" variant="default" onClick={showTableActionsAlert}>
                    Нет
                </Button>,
                <Button key="clear-button" variant="danger" onClick={deleteSelectedUsers}>
                    Да, удалить
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
                // Uncheck table
                setSelectedRows(EMPTY_SET);
            }
        }
    }

    const showConfirmDeleteAlert = useCallback(() => {
        let selectedItemsCount = selectedRowsRef.current.size
        const props = createConfirmDeleteAlertProps(selectedItemsCount)
        showUpdatedAlert(props)
    }, [actionsAlertId]);

    // ALERT HELPERS
    const dismissTableActionAlert = () => {
        let alertId = actionsAlertIdRef.current
        if (alertId) {
            removeAlert(alertId)
            setActionsAlertId(undefined)
            setSelectedRows(EMPTY_SET);
        }
    }

    const actionsAlertIdRef = useRef(actionsAlertId);
    useEffect(() => {
        actionsAlertIdRef.current = actionsAlertId;
    }, [actionsAlertId])

    const selectedRowsRef = useRef(selectedRows);
    useEffect(() => {
        selectedRowsRef.current = selectedRows;
    }, [selectedRows])

    const showTableActionsAlert = useCallback(() => {
        let selectedItems = selectedRowsRef.current
        let count = Array.from(selectedItems).length
        handleAddTableAlert(count)
    }, [actionsAlertId]);

    const handleAddTableAlert = useCallback((count: number) => {
        let props: any = undefined

        const isBlockedUsers = selectedIsBlocked(true)
        if (isBlockedUsers) {
            props = createUnblockAlertProps(count)
        } else {
            props = createBlockAlertProps(count)
        }

        let alertId = actionsAlertIdRef.current
        if (alertId) {
            updateAlert(alertId, {
                customProps: props
            });
        } else {
            const alertId = addCustomAlert(
                TableActionsBanner,
                props,
                {
                    autoClose: false,
                }
            );
            setActionsAlertId(alertId)
        }
    }, [actionsAlertId, data]);

    const showUpdatedAlert = useCallback((customProps: Record<string, any>) => {
        let selectedItemsCount = selectedRowsRef.current.size
        const alertId = actionsAlertIdRef.current
        if (alertId) {
            updateAlert(alertId, {
                customProps: customProps
            });
        }
    }, [actionsAlertId]);

    // TABLE ACTIONS ALERT
    function createBlockAlertProps(count: number) {
        const localStr = locale.selected(count)
        const title = localStr && localStr[0].toUpperCase() + localStr.slice(1);
        const allowBlock = isAllowAction(profile, 'BLOCK_ADS')
        const allowDelete = isAllowAction(profile, 'BLOCK_ADS') // ?

        return {
            count: count,
            title: title,
            buttons: [
                <Button key="clear-button" variant="default" leadingVisual={BlockedIcon} disabled={!allowBlock} onClick={showConfirmBlockAlert}>
                    Заблокировать
                </Button>,
                <Button key="clear-button" variant="default" leadingVisual={TrashIcon} disabled={!allowDelete} onClick={showConfirmDeleteAlert}>
                    Удалить
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
                // Uncheck table
                setSelectedRows(EMPTY_SET);
            }
        }
    }

    function createUnblockAlertProps(count: number) {
        const localStr = locale.selected(count)
        const title = localStr && localStr[0].toUpperCase() + localStr.slice(1);
        const allowBlock = isAllowAction(profile, 'BLOCK_ADS')
        const allowDelete = isAllowAction(profile, 'BLOCK_ADS') // ?

        return {
            count: count,
            title: title,
            buttons: [
                <Button key="clear-button" variant="default" leadingVisual={CodeOfConductIcon} disabled={!allowBlock} onClick={showConfirmUnblockAlert}>
                    Разблокировать
                </Button>,
                <Button key="clear-button" variant="default" leadingVisual={TrashIcon} disabled={!allowDelete} onClick={showConfirmDeleteAlert}>
                    Удалить
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
                // Uncheck table
                setSelectedRows(EMPTY_SET);
            }
        }
    }

    // SELECT ROWS
    const handleSelectAll = useCallback((isSelected: boolean) => {

        let checker = new CheckUserStatus(data?.list || [])
        let isUsersStatusEqual = checker.isAllUsersStatusEqual()

        if (data) {
            if (isSelected) {
                if (isUsersStatusEqual) {
                    setSelectedRows(new Set(data.list.map(row => row.id)));
                } else {
                    setError('Выделение невозможно')
                }
            } else {
                setSelectedRows(EMPTY_SET);
            }
        }
    }, [data]);

    const headerCheckboxState = useMemo(() => {
        if (data && data.list.length > 0) {
            const totalRows = data.list.length;
            const selectedFromCurrentData = data.list.filter(row => selectedRows.has(row.id)).length;

            if (selectedFromCurrentData === 0) {
                return { checked: false, indeterminate: false };
            } else if (selectedFromCurrentData === totalRows && totalRows > 0) {
                return { checked: true, indeterminate: false };
            } else {
                return { checked: false, indeterminate: true };
            }
        }
        return { checked: false, indeterminate: false };
    }, [data, selectedRows]);

    const handleRowSelect = useCallback((rowId: string, isSelected: boolean) => {
        let selectedUsersBlocked = selectedIsBlocked(true)
        let checker = new CheckUserStatus(data?.list || [])
        let currentRowUserBlocked = checker.isBlocked(rowId, true)

        if (selectedRowsRef.current.size === 0) {
            rowSelect(rowId, isSelected)
        } else {
            if (selectedUsersBlocked) {
                if (currentRowUserBlocked) {
                    rowSelect(rowId, isSelected)
                } else {
                    setError('Выделение невозможно')
                }
            } else {
                if (currentRowUserBlocked) {
                    setError('Выделение невозможно')
                } else {
                    rowSelect(rowId, isSelected)
                }
            }
        }
    }, [handleAddTableAlert]);

    const rowSelect = useCallback((rowId: string, isSelected: boolean) => {
        setSelectedRows(prev => {
            const newSelection = new Set(prev);
            if (isSelected) {
                newSelection.add(rowId);
            } else {
                newSelection.delete(rowId);
            }
            return newSelection;
        });

    }, [handleAddTableAlert]);

    useEffect(() => {
        let selectedItemsCount = selectedRows.size
        if (selectedItemsCount > 0) {
            handleAddTableAlert(selectedItemsCount)
        } else {
            if (actionsAlertId) {
                removeAlert(actionsAlertId)
                setActionsAlertId(undefined)
            }
        }
    }, [selectedRows])

    // CHECK USER STATUS
    const selectedIsBlocked = (isBlocked: boolean): boolean => {
        let selectedUsers = selectedRowsRef.current
        const ids = Array.from(selectedUsers);
        let checker = new CheckUserStatus(data?.list || [])
        return checker.allBlockedStatus(ids, isBlocked);
    };

    // EXPORT USERS
    const downloadUsers = async () => {
        try {
            const result = await exportAction(exportUsers, "users", "xlsx");
            if (result.success) {
                setSuccess('Загрузка списка пользователей успешно началась');
            }
            // Ошибку можно не показывать т.к toast вызовется уже в серверном слое
        } catch (err) { }
    }

    useEffect(() => {
        queryClient.invalidateQueries({ queryKey: [searchQuery, pageIndex] })
    }, [pageIndex]);

    const clearSearchInput = () => {
        setSearchQuery("")
    }

    return (
        <RegularPageLayout
            header={
                <Stack>
                    <Stack justify="space-between" direction="horizontal">
                        <Segment items={usersTabsData(profile)} />
                        <Stack direction="horizontal">
                            <TextInput
                                leadingVisual={SearchIcon}
                                trailingAction={
                                    searchQuery.length > 0 ? (
                                        <TextInput.Action
                                            onClick={() => {
                                                setSearchQuery('');
                                            }}
                                            icon={XCircleFillIcon}
                                            aria-label="Очистить"
                                        />
                                    ) : undefined
                                }
                                value={searchQuery}
                                placeholder='Поиск'
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                }}
                            />

                            <ActionMenu>
                                <ActionMenu.Anchor>
                                    <IconButton icon={FilterIcon} aria-label="Фильтр" />
                                </ActionMenu.Anchor>
                                <ActionMenu.Overlay>
                                    <ActionList
                                        selectionVariant="single"
                                        role="menu"
                                        aria-label="Item"
                                        className='w-[232px]'
                                    >
                                        {filterItems.map((item, index) => (
                                            <ActionList.Item
                                                key={index}
                                                role="menuitemradio"
                                                selected={index === selectedIndex}
                                                aria-checked={index === selectedIndex}
                                                onSelect={() => setSelectedFilter(item.filter)}
                                            >
                                                <Stack align="center" direction="horizontal" gap='none'>
                                                    <div className='text-[#59636E] mr-2' >{item.icon}</div>
                                                    {item.label}
                                                </Stack>
                                            </ActionList.Item>
                                        ))}
                                    </ActionList>
                                </ActionMenu.Overlay>
                            </ActionMenu>

                            <IconButton
                                icon={DownloadIcon}
                                aria-label="Выгрузить пользователей"
                                loading={isLoadingExport}
                                onClick={downloadUsers} />
                        </Stack>
                    </Stack>
                </Stack>
            }
            content={
                <Stack>
                    {isError && !isLoading && createErrorPlaceholder()}
                    {isLoading && <Table.Container>
                        <Table.Skeleton
                            aria-labelledby="users-loading"
                            rows={pageSize}
                            columns={usersTableConfig().map((el) => {
                                return {
                                    header: el.header,
                                    id: el.field,
                                }
                            })}
                        />
                    </Table.Container>}
                    {/*<h1>Page {pageIndex}, {data?.list.length}</h1>*/}
                    {isFetched && data?.list.length === 0 && (
                        <Placeholder
                            icon={<XCircleIcon size={24} className='opacity-40' />}
                            header='Ничего не было найдено'
                            description={emptyListDescription}
                            button={searchQuery.length > 0 && <Button
                                variant="default"
                                leadingVisual={XIcon}
                                onClick={clearSearchInput}>
                                Очистить</Button>}
                        />
                    )}
                    {isFetched && data && data.list.length > 0 &&
                        <Table.Container>
                            <DataTable
                                aria-labelledby="users-table"
                                data={data.list}
                                columns={[
                                    {
                                        id: 'select',
                                        header: (() => <Checkbox
                                            checked={headerCheckboxState.checked}
                                            indeterminate={headerCheckboxState.indeterminate}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handleSelectAll(e.target.checked)
                                            }
                                            aria-label="Выделить все строки"
                                        />),
                                        field: undefined,
                                        rowHeader: true,
                                        maxWidth: 40,
                                        renderCell: (row) => {
                                            const user = row as User;
                                            return (
                                                <Checkbox
                                                    checked={selectedRows.has(user.id)}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        handleRowSelect(user.id, e.target.checked)
                                                    }
                                                    aria-label={`Выделить ${user.username}`}
                                                />
                                            )
                                        }
                                    },
                                    {
                                        id: 'id',
                                        header: 'ID',
                                        rowHeader: true,
                                        renderCell: (row) => {
                                            const user = row as User;
                                            return <button
                                                onClick={() => navigate('/users/user?id=' + row.id)}>
                                                {`ID${user.number ?? user.id}`}
                                            </button>
                                        }
                                    },
                                    {
                                        id: 'name',
                                        rowHeader: true,
                                        header: (() =>
                                            <button onClick={() => handleSort('username')}>
                                                <Stack align="center" direction="horizontal" gap='none'>
                                                    {getSortIcon('username')}
                                                    <Text className="ml-[4.5px]">{getSortText('username', 'Имя', 'А-Я', 'Я-А')}</Text>
                                                </Stack>
                                            </button>
                                        ),
                                        renderCell: (row) => {
                                            const user = row as User;
                                            return <button
                                                onClick={() => navigate('/users/user?id=' + row.id)}>
                                                {user.username}
                                            </button>
                                        }
                                    },
                                    {
                                        id: 'phone',
                                        header: (() =>
                                            <button onClick={() => handleSort('phone')}>
                                                <Stack align="center" direction="horizontal" gap='none'>
                                                    {getSortIcon('phone')}
                                                    <Text className="ml-[4.5px]">{getSortText('phone', 'Номер телефона')}</Text>
                                                </Stack>
                                            </button>
                                        ),
                                        renderCell: (row) => {
                                            const user = row as User;
                                            return user.phone
                                        }
                                    },
                                    {
                                        id: 'email',
                                        header: (() =>
                                            <button onClick={() => handleSort('email')}>
                                                <Stack align="center" direction="horizontal" gap='none'>
                                                    {getSortIcon('email')}
                                                    <Text className="ml-[4.5px]">{getSortText('email', 'E-mail')}</Text>
                                                </Stack>
                                            </button>
                                        ),
                                        renderCell: (row) => {
                                            const user = row as User;
                                            return user.email
                                        }
                                    },
                                    {
                                        id: 'status',
                                        header: ('Статус'),
                                        renderCell: (row) => {
                                            const user = row as User;
                                            const isBlocked = user.isBlocked
                                            return (<Label variant={isBlocked ? 'danger' : 'success'} size="small">{isBlocked ? 'Заблокированный' : 'Активный'}</Label>)
                                        }
                                    },
                                    {
                                        id: 'address',
                                        header: 'Адрес',
                                        renderCell: (row) => {
                                            const user = row as User;
                                            const country = user.address?.country?.title || '';
                                            const city = user.address?.city?.title || '';

                                            const addressParts = [country, city].filter(Boolean);

                                            if (addressParts.length === 0) {
                                                return (<></>)
                                            }

                                            return (
                                                <span>{addressParts.join(', ')}
                                                </span>
                                            );
                                        }
                                    },
                                    {
                                        id: 'date',
                                        header: (() =>
                                            'Дата регистрации'
                                            /*
                                            <button onClick={() => handleSort('created_at')}>
                                                <Stack align="center" direction="horizontal" gap='none'>
                                                    {getSortIcon('created_at')}
                                                    <Text className="ml-[4.5px]">{getSortText('created_at', 'Дата регистрации')}</Text>
                                                </Stack>
                                            </button>*/
                                        ),
                                        renderCell: (row) => {
                                            const user = row as User;
                                            return formatDate(user.createdAt)
                                        }
                                    }
                                ]}
                            />
                        </Table.Container>
                    }
                    {isFetched && data && data.list.length > 0 &&
                        (<div className="sticky bottom-0 bg-white border-t border-gray-200 z-10 -mt-4">
                            <Table.Pagination aria-label="pagination" pageSize={pageSize} totalCount={data.totalCount} onChange={({
                                pageIndex
                            }) => {
                                setPageIndex(pageIndex + 1);
                            }} defaultPageIndex={pageIndex - 1} />
                        </div>
                        )}
                </Stack>
            }
        />

    )
}